import {
  findProductForItem,
  resolveProductPrice,
  type PriceCatalogVersion,
  type Product,
  type QuoteOption,
} from '@quotation/domain';

export interface QuickQuoteResult {
  query: string;
  quantity: number;
  selected: QuickQuotePrice;
  related: QuickQuotePrice[];
}

export interface QuickQuotePrice {
  product: Product;
  displayModel: string;
  configurationText: string;
  factoryUnitPriceMinor: number;
  marketGuideUnitPriceMinor: number;
  realCostUnitPriceMinor?: number;
  vendorTablePriceMinor?: number;
  sourcePriceLabel?: string;
  costDiscountBps?: number;
  factoryMarkupBps?: number;
  marketGuideMarkupBps?: number;
  trace: string[];
}

function normalize(value: string): string {
  return value.toLowerCase().replace(/[\s\-_/（）()]/g, '');
}

function chineseQuantity(value: string): number {
  const map: Record<string, number> = { 一: 1, 二: 2, 两: 2, 三: 3, 四: 4, 五: 5, 六: 6, 七: 7, 八: 8, 九: 9, 十: 10 };
  return map[value] ?? 1;
}

export function parseQuickQuoteInput(value: string): { query: string; quantity: number } {
  const quantityMatch = value.match(/(\d+(?:\.\d+)?|[一二两三四五六七八九十]+)\s*(?:台|套|件|个|组)/);
  const quantity = quantityMatch
    ? (/^\d/.test(quantityMatch[1]) ? Number(quantityMatch[1]) : chineseQuantity(quantityMatch[1]))
    : 1;
  const query = value
    .replace(/(\d+(?:\.\d+)?|[一二两三四五六七八九十]+)\s*(?:台|套|件|个|组)/g, '')
    .trim();
  return { query, quantity: Number.isFinite(quantity) && quantity > 0 ? quantity : 1 };
}

function modelKey(product: Product): string {
  return normalize(product.model ?? product.sku);
}

function displayModel(product: Product): string {
  const key = modelKey(product);
  const candidates = [product.sku, ...product.aliases]
    .filter((value): value is string => Boolean(value))
    .filter((value) => normalize(value) === key || normalize(value).includes(key));
  return candidates.sort((left, right) => left.length - right.length)[0] ?? product.model ?? product.sku;
}

function configurationText(product: Product): string {
  const values = (product.configuration ?? []).map((option: QuoteOption) => {
    if (typeof option === 'string') return option;
    return [option.name, option.brand, option.model].filter(Boolean).join(' ');
  }).filter(Boolean);
  return values.length > 0 ? values.slice(0, 6).join(' · ') : product.specification;
}

function priceRow(product: Product, quantity: number, catalog: PriceCatalogVersion): QuickQuotePrice {
  const factory = resolveProductPrice(product, quantity, 'factory', catalog.vendorPolicies);
  const market = resolveProductPrice(product, quantity, 'market_guide', catalog.vendorPolicies);
  const policy = product.vendorPolicyId
    ? catalog.vendorPolicies?.find((candidate) => candidate.id === product.vendorPolicyId)
    : catalog.vendorPolicies?.find((candidate) => candidate.vendorName === product.vendorName);
  return {
    product,
    displayModel: displayModel(product),
    configurationText: configurationText(product),
    factoryUnitPriceMinor: factory.unitPriceMinor,
    marketGuideUnitPriceMinor: market.unitPriceMinor,
    realCostUnitPriceMinor: factory.unitCostMinor,
    vendorTablePriceMinor: product.tablePriceMinor,
    sourcePriceLabel: policy?.sourcePriceLabel,
    costDiscountBps: policy?.costDiscountBps,
    factoryMarkupBps: policy?.factoryMarkupBps,
    marketGuideMarkupBps: policy?.marketGuideMarkupBps,
    trace: factory.trace,
  };
}

export function buildQuickQuote(catalog: PriceCatalogVersion, value: string): QuickQuoteResult | undefined {
  const { query, quantity } = parseQuickQuoteInput(value);
  if (!query) return undefined;
  const product = findProductForItem({
    id: 'quick-quote',
    rawText: query,
    quantity,
    confidence: 1,
    sourceRefs: [],
  }, catalog.products);
  if (!product) return undefined;

  const selectedKey = modelKey(product);
  const relatedProducts = catalog.products
    .filter((candidate) => modelKey(candidate).includes(selectedKey))
    .filter((candidate, index, values) => values.findIndex((item) => item.id === candidate.id) === index)
    .sort((left, right) => left.name.localeCompare(right.name, 'zh-CN') || left.id.localeCompare(right.id));
  const related = relatedProducts.map((candidate) => priceRow(candidate, quantity, catalog));
  const selected = related.find((row) => row.product.id === product.id) ?? related[0];
  return selected ? { query, quantity, selected, related } : undefined;
}
